import React from 'react';
import PropTypes from 'prop-types';
import { Utils } from 'poly-js-utils';
import classNames from 'classnames';

import { connectToJSContext, getSlugUrl } from '../utils.jsx';
import ScrollToLink from '../scroll_to_link/index.jsx';
import Affix from '../affix/index.jsx';
import LanguageSelector from './language_selector.jsx';
import WarningMessage from './warning_message.jsx';
import ProfileDesignations from '../profile/profile_designations';

import {
    isTargetInternalAndEmpty,
    isContactModulePresent,
    isNavElementCurrentPage,
    getContactFormPageUrl
} from './selectors.js';
import { NAVIGATION, ANIMATION_SCROLL_TIME } from '../constants';
import { addQueryParam } from '../helpers';
import LazyImg from '../lazy_img/index.jsx';

const SecondaryNavigationItem = props => {
    const {
        url,
        openInNewWindow,
        text,
        alt,
        isExternal,
        isExternalUrlWithReference,
        slug
    } = props;
    const extendedUrl = isExternal && isExternalUrlWithReference ?
        addQueryParam(url, 'referenceId', slug) : url;
    return (
        <a
            className="navigation-top-menu__link"
            href={extendedUrl}
            target={openInNewWindow ? '_blank' : '_self'}
            rel={openInNewWindow ? 'noopener' : null}
            aria-label={alt}
        >
            {text}
        </a>
    );
};

const SecondaryNavigation = props => (
    <div className="navigation-top-menu hidden-xs">
        <div className="container">
            <div className="row">
                {props.secondaryNavigationLinks.map((link, i) => (
                    <SecondaryNavigationItem
                        key={i}
                        slug={props.slug}
                        isExternalUrlWithReference={props.isExternalUrlWithReference}
                        {...link}
                    />
                ))}
                {props.alternate_slug && props.languages &&
                    <LanguageSelector {...props} />
                }
            </div>
        </div>
    </div>
);

const MobileLanguageSwitcher = props => {
    const { languages, current_language_tag, alternate_slug, orgLink } = props;
    const currentLanguage = languages.find(language => language.language_tag !== current_language_tag);
    const alternateUrl = getSlugUrl(orgLink, alternate_slug);
    return (
        <a href={alternateUrl}>{currentLanguage.language_name}</a>
    );
};

const NavLink = props => {
    const {
        url,
        label,
        alt,
        isCurrent,
        openInNewWindow,
        dataAttributes,
        slug,
        is_external,
        isExternalUrlWithReference,
        subNavigationLinks
    } = props;
    const isCurrentClassName = isCurrent ? 'nav-link-current ' : '';
    const navLinkClassName = `nav-link-${label.toLowerCase().replace(/ /g, '-')}`;
    const navLinkClasses = isCurrentClassName + navLinkClassName;
    const extendedUrl = is_external && isExternalUrlWithReference ?
        addQueryParam(url, 'referenceId', slug) : url;

    return (
        <li>
            <a
                href={extendedUrl}
                className={navLinkClasses}
                target={openInNewWindow ? '_blank' : '_self'}
                rel={openInNewWindow ? 'noopener' : null}
                aria-label={alt}
                {...dataAttributes}
            >
                {label}
            </a>
            {
                Array.isArray(subNavigationLinks) &&
                subNavigationLinks.length > 0 &&
                <div className="sub-navigation-wrapper">
                    <ul className="sub-navigation">
                        {subNavigationLinks.map((item, i) => (
                            <NavLink key={item.url + i} {...item} />
                        ))}
                    </ul>
                </div>
            }
        </li>
    );
};

const NavLinks = props => {
    const {
        className,
        navigationItems,
        contactPageUrl,
        slug,
        isExternalUrlWithReference
    } = props;
    return (
        <ul className={className}>
            {
                navigationItems &&
                navigationItems.map((item, i) => (
                    <NavLink
                        key={i}
                        url={item.url}
                        is_external={item.is_external}
                        slug={slug}
                        isExternalUrlWithReference={isExternalUrlWithReference}
                        label={item.label}
                        alt={item.alt}
                        isCurrent={item.isCurrent}
                        openInNewWindow={item.openInNewWindow}
                        dataAttributes={item.dataAttributes}
                        subNavigationLinks={item.subNavigationLinks}
                    />
                ))
            }
            {props.contactModulePresent ?
                <li>
                    <ScrollToLink
                        href={`#contact-form`}
                        callback={props.closeMenu}
                        className="hidden-sm hidden-md hidden-lg"
                    >
                        {props.contactLabel}
                    </ScrollToLink>
                </li> :
                <li>
                    <a
                        href={contactPageUrl}
                        className="hidden-sm hidden-md hidden-lg"
                    >
                        {props.contactLabel}
                    </a>
                </li>
            }
            {props.includeSecondaryLinks &&
                props.secondaryNavigationLinks.map((link, i) => (
                    <li key={i}><SecondaryNavigationItem {...link} /></li>
                ))
            }
            {props.mobileMenu && props.alternate_slug && props.languages &&
                <li><MobileLanguageSwitcher {...props} /></li>
            }
        </ul>
    );
};

const ContactButton = ({
    contactModulePresent,
    contactLabel,
    contactPageUrl
}) => {
    return contactModulePresent ? (
        <ScrollToLink
            href="#contact-form"
            className="btn btn-navigation-contact-us hs-smooth-scroll"
            animationTime={ANIMATION_SCROLL_TIME}
            label={contactLabel}
        >
            {contactLabel}
        </ScrollToLink>
    ) : (
        <a
            href={contactPageUrl}
            className="btn btn-navigation-contact-us hs-smooth-scroll"
            label={contactLabel}
        >
            {contactLabel}
        </a>
    );
};


class Navigation extends React.Component {

    menu = document.querySelector(NAVIGATION.SELECTOR.CONTAINER);
    state = {
        open: false
    };

    componentDidMount() {
        if (this._sideMenuToggle) {
            window.addEventListener('orientationchange', this.closeMenu);

            window.addEventListener('resize', this.closeMenu);

            this.menu.addEventListener('transitionend', () => {
                if (!this.state.open) {
                    this.menu.classList.remove(NAVIGATION.CLASS_NAME.CLOSE);
                }
            });
        }
    }

    openMenu = () => {
        this.setState(() => ({
            open: true
        }));
        this.menu.classList.add(NAVIGATION.CLASS_NAME.OPEN);
    };

    closeMenu = () => {
        if (this.state.open) {
            this.setState(() => ({
                open: false
            }));
            this.menu.classList.remove(NAVIGATION.CLASS_NAME.OPEN);
            this.menu.classList.add(NAVIGATION.CLASS_NAME.CLOSE);
        }
    };

    toggleMenu = () => {
        if (this.state.open) {
            this.closeMenu();
        } else {
            this.openMenu();
        }
    };

    render() {
        const {
            showSecondaryNavigation,
            navigationCloseButton,
            logoHeader,
            logoUrl,
            logoHeaderAltAttr,
            affixOffset,
            slug,
            isExternalUrlWithReference,
            showHMC,
            appendNameAfterHeaderLogo,
            showDesignationsInHeader,
            agentName,
            hmcSubtitle,
            warningMessageEnabled,
            isContactLast,
            skipNavigationLabel
        } = this.props;

        const Links = () => (
            <NavLinks className="nav hsct-navbar-nav hidden-xs" {...this.props} />
        );

        const Header = () => (
            <div className="hsct-navbar-header">
                <button
                    type="button"
                    className="hsct-navbar-toggle"
                    ref={e => this._sideMenuToggle = e}
                    onClick={this.toggleMenu}
                >
                    <span className="sr-only">{NAVIGATION.SCREEN_READER_ONLY_NAV_TEXT}</span>
                    {this.state.open ?
                        navigationCloseButton
                        :
                        <span className="fa fa-bars" aria-hidden="true" />
                    }
                </button>
                <a
                    href={extendedLogoUrl}
                    target={logoUrl.open_in_new_window ? "_blank" : "_self"}
                    rel="noopener"
                    className="hs-brand-logo"
                >
                    <LazyImg
                        className={classNames('hs-brand-logo__image', {
                            'after-logo-content': showHMC || appendNameAfterHeaderLogo
                        })}
                        src={logoHeader}
                        alt={logoHeaderAltAttr || ""}
                    />
                    {(showHMC || appendNameAfterHeaderLogo) &&
                        <span className="after-logo-text">
                            {showHMC && (hmcSubtitle || '')}
                            {appendNameAfterHeaderLogo && agentName}
                            {appendNameAfterHeaderLogo && showDesignationsInHeader &&
                                <ProfileDesignations isInline={true} />}
                        </span>}
                </a>
            </div>
        );

        const sidrClass = classNames('sidr', 'right', 'visible-xs', { 'sidr--open': this.state.open });
        const extendedLogoUrl = logoUrl.is_external && isExternalUrlWithReference ?
            addQueryParam(logoUrl.url, 'referenceId', slug) : logoUrl.url;

        return (
            <div className="hs-navigation" role="navigation" aria-label="utility top naviagtion">
                {skipNavigationLabel &&
                    <div id="skiptocontent"><a href="#maincontent">{skipNavigationLabel}</a></div>
                }
                {showSecondaryNavigation &&
                    <SecondaryNavigation {...this.props} />
                }
                <div className="navbar-section-wrapper">
                    <Affix offset={affixOffset} className="navbar-section">
                        <div className="hsct-container">
                            <div className="row">
                                {isContactLast ? (
                                    <nav className="hsct-navbar">
                                        <Header />
                                        <Links />
                                        <ContactButton {...this.props} />
                                    </nav>
                                ) : (
                                    <nav className="hsct-navbar">
                                        <Header />
                                        <ContactButton {...this.props} />
                                        <Links />
                                    </nav>
                                )}
                            </div>
                        </div>
                    </Affix>
                </div>
                <div id="sidr" className={sidrClass}>
                    <NavLinks {...this.props} closeMenu={this.closeMenu} mobileMenu={true} includeSecondaryLinks />
                </div>
                {warningMessageEnabled &&
                    <WarningMessage />
                }
            </div>
        );
    }
}

Navigation.propTypes = {
    secondaryNavigationLinks: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string,
            openInNewWindow: PropTypes.bool,
            text: PropTypes.string,
            alt: PropTypes.string,
            isExternal: PropTypes.bool
        })
    ).isRequired,
    navigationItems: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string.isRequired,
            is_external: PropTypes.bool,
            label: PropTypes.string.isRequired,
            alt: PropTypes.string,
            openInNewWindow: PropTypes.bool,
            isCurrent: PropTypes.bool
        })
    ).isRequired,
    showSecondaryNavigation: PropTypes.bool.isRequired,
    baseLink: PropTypes.string.isRequired,
    orgLink: PropTypes.string.isRequired,
    hasTeamMembers: PropTypes.bool,
    logoHeader: PropTypes.string.isRequired,
    logoUrl: PropTypes.shape({
        url: PropTypes.string,
        open_in_new_window: PropTypes.bool,
        is_external: PropTypes.bool
    }),
    logoHeaderAltAttr: PropTypes.string.isRequired,
    navigationCloseButton: PropTypes.string,
    skipNavigationLabel: PropTypes.string,
    contactLabel: PropTypes.string,
    isContactLast: PropTypes.bool,
    affixOffset: PropTypes.number,
    menu: PropTypes.element,
    languages: PropTypes.arrayOf(
        PropTypes.shape({
            language_name: PropTypes.string,
            language_tag: PropTypes.string
        })
    ),
    current_language_tag: PropTypes.string,
    slug: PropTypes.string,
    alternate_slug: PropTypes.string,
    isExternalUrlWithReference: PropTypes.bool
};

Navigation.defaultProps = {
    navigationCloseButton: NAVIGATION.DEFAULT_PROPS.NAVIGATION_CLOSE_BUTTON,
    contactLabel: NAVIGATION.DEFAULT_PROPS.NAVIGATION_CONTACT_LABEL
};

const mapNavigationItems = (state, props, navigationLinks) => {
    return navigationLinks.filter((item) =>
        (isTargetInternalAndEmpty(state, item.url))).map(item =>
        ({
            url: Utils.selectBoxIsTrue(item.is_external) ? item.url : `${state.base_link}${item.url}`,
            is_external: Utils.selectBoxIsTrue(item.is_external),
            label: item.text,
            alt: item.alt_text,
            openInNewWindow: Utils.selectBoxIsTrue(item.open_in_new_window),
            isCurrent: isNavElementCurrentPage(props, item),
            dataAttributes: /(product)/i.test(item.url) && {
                "data-product-name": "",
                "data-click-type": "product-view-all"
            },
            subNavigationLinks: mapNavigationItems(state, props, (item.sub_navigation_links || []))
        })
    );
};

const mapStateToProps = (state, props) => {
    const {
        warning_message_enabled
    } = state.theme_data.warning_msg_options || {};
    return {
        showSecondaryNavigation: Utils.selectBoxIsTrue(state.theme_data.nav.show_utility_bar),
        secondaryNavigationLinks: (state.theme_data.nav.custom_utility_bar_links || []).map((link) =>
            ({
                url: link.url,
                text: link.text,
                alt: link.alt_text,
                openInNewWindow: Utils.selectBoxIsTrue(link.open_in_new_window),
                isExternal: Utils.selectBoxIsTrue(link.is_external)
            })
        ),
        baseLink: state.base_link,
        orgLink: state.org_link,
        hasTeamMembers: state.profile.team_members && Boolean(state.profile.team_members.length),
        logoHeader: state.profile.nav_logo || state.theme_data.nav.logo_header,
        logoUrl: {
            url: (state.profile.nav_logo_url && state.profile.nav_logo_url.url) ||
                (state.theme_data.nav.logo_url && state.theme_data.nav.logo_url.url),
            open_in_new_window: Utils.selectBoxIsTrue(
                (state.profile.nav_logo_url && state.profile.nav_logo_url.open_in_new_window) ||
                (state.theme_data.nav.logo_url && state.theme_data.nav.logo_url.open_in_new_window)
            ),
            is_external: Utils.selectBoxIsTrue(
                (state.profile.nav_logo_url && state.profile.nav_logo_url.is_external) ||
                (state.theme_data.nav.logo_url && state.theme_data.nav.logo_url.is_external)
            )
        },
        logoHeaderAltAttr: state.profile.nav_logo_alt || state.theme_data.nav.logo_alt,
        navigationItems: mapNavigationItems(
            state,
            props,
            ((state.theme_data.nav && state.theme_data.nav.main_navigation_links) || [])
        ),
        navigationCloseButton: state.theme_data.nav && state.theme_data.nav.close_button,
        contactLabel: state.theme_data.nav.cta_text,
        skipNavigationLabel: state.theme_data.nav.skipNavigationText,
        isContactLast: Utils.selectBoxIsTrue(state.theme_data.nav.is_cta_last),
        affixOffset: 40,
        contactModulePresent: isContactModulePresent(state.theme_data, props.currentPage),
        contactPageUrl: getContactFormPageUrl(state.theme_data, state.base_link),
        languages: state.theme_data.nav.languages || null,
        current_language_tag: state.theme_data.meta.language_tag || null,
        slug: state.profile.slug || null,
        alternate_slug: state.profile.alternate_slug || null,
        isExternalUrlWithReference: Utils.selectBoxIsTrue(state.theme_data.nav.is_external_url_with_reference),
        appendNameAfterHeaderLogo: Utils.selectBoxIsTrue(state.theme_data.append_name_after_header_logo),
        showDesignationsInHeader: Utils.selectBoxIsTrue(state.theme_data.show_designations_in_header),
        agentName: `${state.profile.first_name} ${state.profile.last_name}`,
        showHMC: Utils.selectBoxIsTrue(state.theme_data.is_wellsfargo),
        hmcSubtitle: state.profile.title || '',
        warningMessageEnabled: Utils.selectBoxIsTrue(warning_message_enabled)
    };
};

export default connectToJSContext(mapStateToProps)(Navigation);



// WEBPACK FOOTER //
// ./components/navigation/index.jsx